'use client';

import * as React from 'react';
import { useTranslations } from 'next-intl';
import { DiscountCode } from '@/lib/types/customer/restaurant/StoreProfile';
import { XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';

import * as pixel from '@/lib/utils/fpixel';
import { safeCopyToClipboard } from '@/lib/helpers/functions';
import useAppLocale from '@/hooks/useAppLocale';
import { STOREFRONT } from '@/lib/utils/translation';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import {
  getClickIdForEvent,
  markEventAsTracked,
} from '@/lib/utils/adcashTracking';

type DiscountCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data: DiscountCode;
  storeName: string;
  onlineShopLink: string;
  storeLogo?: string;
};

export default function DiscountCodeModal({
  isOpen,
  onClose,
  data,
  storeName,
  onlineShopLink,
  storeLogo,
}: DiscountCodeModalProps) {
  const { lang } = useAppLocale();
  const t = useTranslations();
  const [isCopied, setIsCopied] = React.useState(false);

  const handleCopyCode = async () => {
    const isSuccess = await safeCopyToClipboard({
      text: data.code,
      lang: lang as 'en' | 'de',
      fallbackMessage: t(`${STOREFRONT.STORE_PROFILE}.copy_didnt_work`),
    });

    if (!isSuccess) return;

    // Existing Facebook Pixel tracking
    pixel.trackCustomToPixel(pixel.B2C_FB_PIXEL_ID, 'CopyDiscountCode', {
      code: data.code,
    });

    // Adcash tracking (fire and forget) - only track if not already tracked
    const adcashClickId = getClickIdForEvent('button_click');
    if (adcashClickId) {
      fetch('/api/adcash/button-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clickId: adcashClickId,
          buttonContext: 'discount_copy',
          metadata: { code: data.code, store: storeName },
        }),
        keepalive: true,
      })
        .then(() => markEventAsTracked('button_click'))
        .catch((err) => console.error('[Adcash] Button tracking failed:', err));
    }

    setIsCopied(true);
  };

  const handleContinueToStore = () => {
    if (!onlineShopLink) return;
    window.open(onlineShopLink, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/25"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative mx-4 w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-xl"
          >
            {/* Modal Content */}
            <div className="p-8 pb-6">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute right-6 top-6 text-gray-400 transition-colors hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Store Logo/Name */}
              <div className="mb-6 flex items-center justify-center">
                <div className="flex items-center space-x-3">
                  <div
                    className={cn(
                      'flex h-12 w-12 items-center justify-center overflow-hidden rounded-[5px] border border-[#E5E7EB]',
                      !storeLogo && 'bg-gray-100'
                    )}
                  >
                    {storeLogo ? (
                      <div className="">
                        <Image
                          src={storeLogo}
                          alt={`${storeName} brand logo`}
                          width={48}
                          height={48}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    ) : (
                      <span className="text-sm font-semibold text-gray-600">
                        {storeName.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {storeName}
                  </h2>
                </div>
              </div>

              {/* Title */}
              {/* <h1 className="mb-8 text-center text-h3 font-bold text-gray-900">
                {data.title ||
                  `Enjoy ${data.cashback} Off First Orders at ${storeName}`}
              </h1> */}
              <div className="mb-8 text-center">
                <h3 className="text-h4 font-bold text-paragraph">
                  {data.cashback} {t('common.discount_code')}
                </h3>
                {data && data.description && (
                  <div
                    className="w-full min-w-full text-h8 font-normal leading-[150%] text-paragraph"
                    dangerouslySetInnerHTML={{
                      __html: data.description,
                    }}
                  />
                )}
              </div>

              {/* Code Section */}
              <div className="mb-6">
                <div className="flex items-stretch overflow-hidden rounded-lg">
                  <div className="flex flex-1 items-center justify-center rounded-l-lg border-2 border-r-0 border-dashed border-pink-300 bg-pink-50 px-4 py-6">
                    <span className="text-xl font-bold text-gray-900">
                      {data.code}
                    </span>
                  </div>
                  <button
                    onClick={handleCopyCode}
                    className="rounded-r-lg bg-primary-800 px-8 py-6 font-semibold text-white transition-colors duration-200 hover:bg-primary-900"
                  >
                    {isCopied
                      ? t(`${STOREFRONT.STORE_PROFILE}.copied`)
                      : t(`${STOREFRONT.STORE_PROFILE}.copy`)}
                  </button>
                </div>

                <p className="mt-3 text-center text-sm text-gray-600">
                  {t(`${STOREFRONT.STORE_PROFILE}.copy_and_paste_the_code`, {
                    shop_name: storeName,
                  })}
                </p>
              </div>
            </div>

            {/* Continue Button - Footer */}
            <button
              onClick={handleContinueToStore}
              className="flex w-full items-center justify-center space-x-2 bg-primary-50 px-8 py-6 font-semibold text-gray-800 transition-colors duration-200 hover:bg-primary-100"
            >
              <span>
                {t(`${STOREFRONT.STORE_PROFILE}.continue_to_shop`, {
                  shop_name: storeName,
                })}
              </span>
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
